import { Element } from "@craftjs/core";
import { ASSETS } from "../utils/config";
import { withWhiteSwatch } from "./swatchPalette";
import {
  ContainerWidget,
  TextWidget,
  ImageWidget,
  DividerWidget,
  VideoWidget,
  ImageWithTextWidget,
  Buttons,
  LinkWheelWidget,
  ImageGridWidget,
  SocialLinksWidget,
  DocumentWidget,
  AudioWidget,
  GraphWidget,
  TableWidget,
  PollWidget,
  FormBlockWidget,
  CarouselWidget,
  TitleBarWidget,
} from "../components/SegmentBuilder/CodeBlocks";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { KeyboardArrowUp } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Accordion } from "../components/SegmentBuilder/CodeBlocks/Accordian";
import { colors } from "../utils";
import { BUILD_MENU_V2_ENABLED } from "./featureFlags";
import { CAROUSEL_BLOCK_ENABLED, TITLE_BAR_BLOCK_ENABLED, TABLE_BLOCK_ENABLED } from "./featureFlags";
import { widgetNames } from "./widgetNames";

// Inline (self-contained) line-art icons for the two NEW Premium blocks — keeps
// the build free of a new asset import while matching the deck's block cards.
const POLL_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='24' viewBox='0 0 24 24' fill='none'%3E%3Crect x='4' y='13' width='4' height='7' rx='1' stroke='%23123051' stroke-width='1.6'/%3E%3Crect x='10' y='8' width='4' height='12' rx='1' stroke='%23123051' stroke-width='1.6'/%3E%3Crect x='16' y='4' width='4' height='16' rx='1' stroke='%23123051' stroke-width='1.6'/%3E%3C/svg%3E";
const FORM_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='24' viewBox='0 0 24 24' fill='none'%3E%3Crect x='4' y='3.5' width='16' height='17' rx='2' stroke='%23123051' stroke-width='1.6'/%3E%3Cpath d='M7.5 8h9M7.5 12h9M7.5 16h5' stroke='%23123051' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E";
// Carousel (DL-041): a centre film-strip card flanked by two peeked neighbours —
// the block's own identity as line-art (never emoji), matching the deck.
const CAROUSEL_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Crect x='8' y='5' width='8' height='14' rx='1.5' stroke='%23123051' stroke-width='1.6'/%3E%3Crect x='2.5' y='7.5' width='4' height='9' rx='1' stroke='%23123051' stroke-width='1.4' opacity='0.55'/%3E%3Crect x='17.5' y='7.5' width='4' height='9' rx='1' stroke='%23123051' stroke-width='1.4' opacity='0.55'/%3E%3C/svg%3E";
// Title Bar (wave D2): a heading band — a wide rounded rectangle with a short
// centered text line, the block's own identity as line-art (never emoji).
const TITLEBAR_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Crect x='3' y='8' width='18' height='8' rx='2' stroke='%23123051' stroke-width='1.6'/%3E%3Cpath d='M8.5 12h7' stroke='%23123051' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E";

export const BoxWidthTypes = {
  ContentWidth: { id: 1, image: ASSETS.segmentEditor.ContentWidth },
  FullWidth: { id: 2, image: ASSETS.segmentEditor.FullWidth },
};

const defaultText = <p>Hello there!</p>;
const segmentTools = [
  {
    id: 0,
    title: "Layout",
    backgroundColor: "#FFF5EA",
    elements: [
      {
        id: 0,
        title: "Container",
        image: ASSETS.segmentEditor.ImageGrid,
        component: <Element canvas is={ContainerWidget} padding={20} />,
        dataCy: "toolbox-container",
      },
      {
        id: 1,
        title: "Divider",
        image: ASSETS.segmentEditor.Divider,
        component: <DividerWidget />,
        dataCy: "toolbox-divider",
      },
    ],
  },
  {
    id: 1,
    title: "Content",
    backgroundColor: "#ECF2DA80",
    elements: [
      {
        id: 0,
        title: "Text",
        image: ASSETS.segmentEditor.Text,
        component: <TextWidget />,
        dataCy: "toolbox-text",
      },
      {
        id: 1,
        title: "Image/GIF",
        image: ASSETS.segmentEditor.Image,
        component: <ImageWidget />,
        dataCy: "toolbox-image",
      },
      {
        id: 2,
        title: "Video",
        image: ASSETS.segmentEditor.Video,
        component: <VideoWidget />,
        dataCy: "toolbox-video",
      },
      {
        id: 3,
        title: "Image With Text",
        image: ASSETS.segmentEditor.ImageWithText,
        component: <ImageWithTextWidget />,
        dataCy: "toolbox-image-text",
      },
      {
        id: 4,
        title: "Image Grid",
        image: ASSETS.segmentEditor.GridIcon,
        component: <ImageGridWidget />,
        dataCy: "toolbox-image-grid",
      },
      {
        id: 5,
        title: "Document",
        image: ASSETS.segmentEditor.Document,
        component: <DocumentWidget />,
        dataCy: "toolbox-document",
      },
      {
        id: 6,
        title: "Audio",
        image: ASSETS.segmentEditor.Audio,
        component: <AudioWidget />,
        dataCy: "toolbox-audio",
      },
      {
        id: 7,
        title: "Graph",
        image: ASSETS.segmentEditor.GraphBlock,
        component: <GraphWidget />,
        dataCy: "toolbox-graph",
        // fix-kelly-0930-P0 (Kelly, VP Sales): Graph is a Premium block, same
        // paid-lock pattern as Poll/Form below. BE also refuses saving a
        // GraphWidget node for a below-Premium user -- see
        // subscription/permissions.py assert_premium_blocks_allowed.
        isPremium: true,
      },
      // ISP-4018 Table — flag-gated (TABLE_BLOCK_ENABLED). When OFF this entry is
      // absent, so the Content group is byte-identical to a build without the
      // block and the block cannot be added. The craft.js resolver keeps
      // TableWidget registered unconditionally, so existing saved Table nodes
      // still render. This conditional spread is the SINGLE user-facing gate.
      // Merge-train staging env line: REACT_APP_TABLE_BLOCK=true.
      ...(TABLE_BLOCK_ENABLED
        ? [
            {
              id: 8,
              title: "Table",
              image: ASSETS.segmentEditor.TableIcon,
              component: <TableWidget />,
              dataCy: "toolbox-table",
              // fix-kelly-0930-P0: Table is a Premium block too (same as
              // Graph above / Poll/Form below). BE refuses saving a
              // TableWidget node for a below-Premium user.
              isPremium: true,
            },
          ]
        : []),
      // DL-041 Carousel — flag-gated (CAROUSEL_BLOCK_ENABLED). When OFF this entry
      // is absent, so the Content group is byte-identical to today and the block
      // cannot be added. This conditional spread is the SINGLE user-facing gate.
      ...(CAROUSEL_BLOCK_ENABLED
        ? [
            {
              id: 9,
              title: "Carousel",
              image: CAROUSEL_ICON,
              component: <CarouselWidget />,
              dataCy: "toolbox-carousel",
            },
          ]
        : []),
      // Title Bar (wave D2) — flag-gated (TITLE_BAR_BLOCK_ENABLED). When OFF this
      // entry is absent, so the Content group is byte-identical to today and the
      // block cannot be added. This conditional spread is the SINGLE user-facing gate.
      ...(TITLE_BAR_BLOCK_ENABLED
        ? [
            {
              id: 10,
              title: "Title Bar",
              image: TITLEBAR_ICON,
              component: <TitleBarWidget />,
              dataCy: "toolbox-title-bar",
            },
          ]
        : []),

      // { id: 3, title: "Image Above", image: ASSETS.segmentEditor.ImageAbove },
      // { id: 4, title: "Image Below", image: ASSETS.segmentEditor.ImageBelow },
    ],
  },
  {
    id: 2,
    title: "Interactive",
    backgroundColor: "#F6F7F9",
    elements: [
      {
        id: 0,
        title: "Accordion",
        image: ASSETS.segmentEditor.Accordion,
        component: <Accordion />,
        dataCy: "toolbox-accordion",
      },

      {
        id: 7,
        title: "Link Wheel",
        image: ASSETS.segmentEditor.LinkWheelIcon,
        component: <LinkWheelWidget />,
        dataCy: "toolbox-link-wheel",
      },

      {
        id: 1,
        title: "Button",
        image: ASSETS.segmentEditor.ButtonBlock,
        // component: <Element canvas is={ButtonsWidget} padding={20} />,
        component: <Buttons />,
        dataCy: "toolbox-buttons",
      },
      {
        id: 1,
        title: "Link Social Platforms",
        image: ASSETS.segmentEditor.Social,
        // component: <Element canvas is={ButtonsWidget} padding={20} />,
        component: <SocialLinksWidget />,
        dataCy: "toolbox-social",
      },
      {
        id: 8,
        title: "Poll",
        image: POLL_ICON,
        component: <PollWidget />,
        dataCy: "toolbox-poll",
        isPremium: true,
      },
      {
        id: 9,
        title: "Form",
        image: FORM_ICON,
        component: <FormBlockWidget />,
        dataCy: "toolbox-form",
        isPremium: true,
      },
    ],
  },
];

// Block-picker tile descriptions (nemesis #115) — a plain-language one-liner per
// block, surfaced as a tooltip on each Build Menu tile (Shared/Card) so the
// icon-only tiles are no longer opaque. Keyed by the element `title`; stamped onto
// the element objects below so BOTH the live `segmentTools` and the derived
// `segmentToolsV2` (which references the SAME element objects) inherit them.
export const BLOCK_DESCRIPTIONS = {
  Container: "Group blocks together inside a bordered box.",
  Divider: "A horizontal line to separate content.",
  Text: "Rich text with fonts, colours and formatting.",
  "Image/GIF": "A single image or animated GIF.",
  Video: "Upload a video or embed one from YouTube, Vimeo and more.",
  "Image With Text": "An image with text wrapped alongside it.",
  "Image Grid": "A gallery of images arranged in a grid.",
  Document: "Embed a PDF or Google / Office document.",
  Audio: "Upload audio or embed a Spotify / SoundCloud track.",
  Graph: "A chart to visualise your data.",
  Table: "A data table with rows and columns.",
  Carousel: "A swipeable slideshow of images or cards.",
  "Title Bar": "A heading band to title a section.",
  Accordion: "Expandable sections that show and hide content.",
  "Link Wheel": "Embed and link to another Wheel.",
  Button: "A tappable button that links to a URL.",
  "Link Social Platforms": "Icons linking to your social profiles.",
  Poll: "Let visitors vote on a set of options.",
  Form: "Collect responses with a custom form.",
};

segmentTools.forEach((section) => {
  (section.elements || []).forEach((el) => {
    if (el && el.title && BLOCK_DESCRIPTIONS[el.title]) {
      el.description = BLOCK_DESCRIPTIONS[el.title];
    }
  });
});

// fix-gordon-0929-P0-block-pane-header-icons: the block settings side pane
// (WidgetDisplay -> utils/helpers.js getWidgetDetails) used to keep its OWN
// icon per craft.js resolver name, hardcoded independently of the Build Menu
// tile above. That switch missed every block added after it was written
// (Carousel/Title Bar/Poll/Form/Graph/Accordion/Button all fell to a
// icon:null default) and, for the cases it DID cover, sometimes pointed at a
// completely different asset than the tile (Container's tile uses
// ImageGrid/ContainerV2; the pane used a separate ContainerSetting SVG —
// measured live on web-dev as a mismatched icon). Rather than adding one more
// hardcoded case per block (the exact pattern that keeps regressing), key
// every element by its craft.js resolver name (the same string
// getWidgetDetails receives) and expose ONE lookup from tile title -> tile
// image, so the pane header can never again disagree with its own tile.
const ELEMENT_WIDGET_NAME = {
  Container: widgetNames.CONTAINER,
  Divider: widgetNames.DIVIDER,
  Text: widgetNames.TEXT,
  "Image/GIF": widgetNames.IMAGE,
  Video: widgetNames.VIDEO,
  "Image With Text": widgetNames.IMAGEWITHTEXT,
  "Image Grid": widgetNames.IMAGEGRID,
  Document: widgetNames.DOCUMENT,
  Audio: widgetNames.AUDIO,
  Graph: widgetNames.GRAPH,
  Table: widgetNames.TABLE,
  Carousel: widgetNames.CAROUSEL,
  "Title Bar": widgetNames.TITLE_BAR,
  Accordion: widgetNames.ACCORDION,
  "Link Wheel": widgetNames.LINKWHEEL,
  Button: widgetNames.BUTTON,
  "Link Social Platforms": widgetNames.SOCIALLINKS,
  Poll: widgetNames.POLL,
  Form: widgetNames.FORM,
};

export const TILE_ICON_BY_WIDGET_NAME = {};
segmentTools.forEach((section) => {
  (section.elements || []).forEach((el) => {
    const name = el && el.title && ELEMENT_WIDGET_NAME[el.title];
    if (name) {
      el.widgetName = name;
      TILE_ICON_BY_WIDGET_NAME[name] = el.image;
    }
  });
});

// --- Build Menu V2 (flag-gated) ---------------------------------------------
// Derived from the LIVE `segmentTools` above so EVERY currently-present block —
// including the flag-gated ones (Table / Carousel / Title Bar / Poll / Form) —
// is preserved by construction; nothing is ever dropped. V2 only:
//   (a) re-homes Accordion from Interactive -> Layout (a structural block),
//   (b) re-orders the Content group (text -> heading -> image family -> media
//       -> file -> data),
//   (c) attaches a per-section `iconColor` (orange / green / blue).
// Section membership is otherwise untouched. When the flag is OFF,
// `activeSegmentTools` IS the original `segmentTools` reference, so the palette
// renders byte-identically to today (no iconColor -> Card uses the plain <img>).
const _sectionByTitle = {};
segmentTools.forEach((s) => {
  _sectionByTitle[s.title] = s;
});
const _elementsOf = (title) => _sectionByTitle[title]?.elements || [];
const _findEl = (title, elTitle) =>
  _elementsOf(title).find((el) => el?.title === elTitle);

// (a) Accordion moves Interactive -> Layout (kept only if actually present).
const _accordion = _findEl("Interactive", "Accordion");
const _layoutV2 = [
  ..._elementsOf("Layout"),
  ...(_accordion ? [_accordion] : []),
];

// (b) Content re-order. Listed titles come first in this order; ANY Content
// block not in the list (e.g. a future/flag-gated one) keeps its original
// relative order at the end — so new blocks are never lost.
const _CONTENT_ORDER = [
  "Text",
  "Title Bar",
  "Image/GIF",
  "Image With Text",
  "Image Grid",
  "Carousel",
  "Video",
  "Audio",
  "Document",
  "Graph",
  "Table",
];
const _contentV2 = [
  ..._CONTENT_ORDER.map((t) => _findEl("Content", t)).filter(Boolean),
  ..._elementsOf("Content").filter(
    (el) => !_CONTENT_ORDER.includes(el?.title)
  ),
];

// (c) Interactive keeps its members minus Accordion, order preserved.
const _interactiveV2 = _elementsOf("Interactive").filter(
  (el) => el?.title !== "Accordion"
);

export const segmentToolsV2 = [
  {
    id: 0,
    title: "Layout",
    backgroundColor: "#FFF5EA",
    iconColor: colors.yellowText, // orange
    elements: _layoutV2,
  },
  {
    id: 1,
    title: "Content",
    backgroundColor: "#ECF2DA80",
    iconColor: colors.PrimaryGreen, // green
    elements: _contentV2,
  },
  {
    id: 2,
    title: "Interactive",
    backgroundColor: "#F6F7F9",
    iconColor: colors.darkBlue, // blue
    elements: _interactiveV2,
  },
];

// Single source the Build Menu reads. Identity-equal to `segmentTools` when OFF.
export const activeSegmentTools = BUILD_MENU_V2_ENABLED
  ? segmentToolsV2
  : segmentTools;

export const ToolTabList = [
  {
    label: "Content Blocks",
    value: "0",
    disabled: false,
  },
  {
    label: "Appearance",
    value: "1",
    disabled: true,
  },
];

// widgetNames now lives in its own dependency-free module (src/constants/widgetNames.js)
// with its own test, so the craft.js-resolver-key contract can be asserted without
// dragging this file's CodeBlocks/Froala/CSS graph into a unit test.
// Re-exported here so every existing import of widgetNames from this module's path still works.
export { widgetNames } from "./widgetNames";

export const solidColors = withWhiteSwatch([
  "#E9ECEF",
  "#DEE2E6",
  "#CED4DA",
  "#ADB5BD",
  "#868E96",
  "#495057",
  "#343A40",
  "#212529",
  "#C92A2A",
  "#862E9C",
  "#5F3DC4",
  "#364FC7",
  "#1864AB",
  "#087F5B",
  "#2B8A3E",
  "#5C940D",
  "#E67700",
  "#D9480F",
  "#FA5252",
  "#CC5DE8",
  "#5C7CFA",
  "#339AF0",
  "#20C997",
  "#51CF66",
  "#FCC419",
  "#FF922B",
  "#FFC9C9",
  "#EEBEFA",
  "#D0BFFF",
  "#BAC8FF",
  "#A5D8FF",
  "#96F2D7",
  "#B2F2BB",
  "#D8F5A2",
  "#FFEC99",
  "#FFD8A8",
]);

export const searchEngines = {
  image: [
    { id: 0, key: 1, title: "Images", items: [] },
    { id: 1, key: 2, title: "Gifs", items: [] },
  ],
  images: [
    { id: 0, key: 1, title: "Images", items: [] },
    { id: 1, key: 2, title: "Gifs", items: [] },
  ],
  // gordon-0915-video-block-media-pane-pixabay-embed-first-record-tier: the
  // Video Browse tab was Youtube-only (key 1), and its live scraper
  // (_scrape_youtube_videos, no official API) is currently returning zero
  // results for EVERY query ("cat", "dog", "music" — not just "Architecture"),
  // measured live against app-dev's /api/v2/wheel/get-media/?type=2&source=1.
  // BE already has a working Pixabay video source (VideoSource.PIXABAY=3,
  // yuga_wheel/api/v2/viewsets/wheel/actions/get_media.py) that returns real
  // results (verified live for "Architecture") but was unreachable from the
  // FE — see yuga_wheel/tests/test_get_media_video_source_contract.py's own
  // "unreachable from the current FE" note.
  //
  // FOLLOW-UP (same day): a first cut of this fix added Pixabay as a
  // second, manually-selected engine and left Youtube as the DEFAULT. That
  // does not actually fix Gordon's reported repro — his exact steps
  // (open Video Browse, search "Architecture", do nothing else) still hit
  // the dead Youtube scraper by default and still show "No results found".
  // Pixabay must be the DEFAULT/first entry so the tab that already shows
  // the Pixabay logo/branding actually searches Pixabay out of the box.
  // Youtube stays reachable as a manual fallback via the dropdown (still a
  // real key=1 -> VideoSource.YOUTUBE route per
  // test_get_media_video_source_contract.py, which only pins the BE's
  // source=1 routing and does not care which entry the FE defaults to).
  video: [
    { id: 0, key: 3, title: "Pixabay", items: [] },
    { id: 1, key: 1, title: "Youtube", items: [] },
  ],
};

export const breakpointColumnsObj = {
  default: 4,
  1100: 3,
  700: 2,
  500: 1,
};

export const initialKeywords = [
  { id: 0, title: "Abstract" },
  { id: 1, title: "Animal" },
  { id: 2, title: "Architecture" },
  { id: 3, title: "Art" },
  { id: 4, title: "Business" },
  { id: 5, title: "Colorful" },
  { id: 6, title: "Food" },
  { id: 7, title: "Interior" },
  { id: 8, title: "Minimal" },
  { id: 9, title: "Nature" },
  { id: 10, title: "Plant" },
  { id: 11, title: "Portrait" },
  { id: 12, title: "Space" },
  { id: 13, title: "Technology" },
  { id: 14, title: "Texture" },
  { id: 15, title: "Wallpaper" },
];

export const MEDIA_TYPE = {
  image: 1,
  images: 1,
  video: 2,
  gif: 3,
};

export const ACCORDION_DROPDOWN_BUTTON_STYLE = [
  {
    id: 0,
    key: 0,
    title: "Up / Down",
    icons: [
      {
        id: 0,
        component: <KeyboardArrowUp />,
      },
      {
        id: 1,
        component: <KeyboardArrowDownIcon />,
      },
    ],
  },
  {
    id: 1,
    key: 1,
    title: "Plus / Minus",
    icons: [
      {
        id: 0,
        component: <AddIcon />,
      },
      {
        id: 1,
        component: <RemoveIcon />,
      },
    ],
  },
];

export const VISIBILITY_KEY = {
  PUBLIC: 1,
  PRIVATE: 2,
  HIDDEN: 3,
};

export const GRID_TYPES = {
  STANDARD: "standard",
  QUILTED: "quilted",
  MASONRY: "masonry",
};

export default segmentTools;
